import { AccountId } from '@ply-io/ply-front-shared-utilities';

import { buildCrud, api, nonAuthedApi } from './clients';

const WORKSPACE_DOMAINS_ENDPOINT = 'account-email-domains';

export type AccountEmailDomainId = number;

export type VerifyOtpResponse = {
	is_valid: boolean;
};

export type ReplyToResponse = {
	reply_to: string[];
};

export type UnsubscribeContactResponse = {
	unsubscribe_emails: string[];
};

export type AccountEmailDomainResponse = {
	id: AccountEmailDomainId;
	account: AccountId;
	domain: string;
	verified: boolean;
	dkim: string;
	spf: string;
	list_of_allowed_emails: string[];
	return_path_domain: string;
	postmark_domain_id: string;
	is_ply_mail: boolean;
};

export type AccountEmailDomainPayload = Omit<
	AccountEmailDomainResponse,
	'id' | 'account' | 'verified' | 'dkim' | 'spf' | 'return_path_domain' | 'postmark_domain_id' | 'list_of_allowed_emails'
> & {
	list_of_allowed_emails: AccountEmailDomainResponse['list_of_allowed_emails'];
	return_path_domain?: AccountEmailDomainResponse['return_path_domain'];
};

const verifyDkim = async (id: AccountEmailDomainId): Promise<AccountEmailDomainResponse> =>
	await api.post(`${WORKSPACE_DOMAINS_ENDPOINT}/${id}/verify_dkim/`);

const deleteReplyToEmail = async (email: string): Promise<ReplyToResponse> =>
	await api.delete('accounts/emails/reply-to/', { params: { email } });

const listReplyToEmails = async (): Promise<ReplyToResponse> => await api.get('accounts/emails/reply-to/');

const deleteUnsubscribeContactEmail = async (accountId: number, email: string): Promise<ReplyToResponse> =>
	await api.delete(`accounts/${accountId}/emails/unsubscribe/`, { params: { email } });

const listUnsubscribeContactEmails = async (accountId: number): Promise<ReplyToResponse> =>
	await api.get(`accounts/${accountId}/emails/unsubscribe/`);

const sendVerifyEmail = async (email: string): Promise<AccountEmailDomainResponse> =>
	await api.post('accounts/emails/send/', { email_to_verify: email });

const verifyReplyToEmailOtp = async (accountId: number, otp: number): Promise<AccountEmailDomainResponse> =>
	await api.post(`accounts/${accountId}/emails/reply-to/verify/`, { otp });

const verifyUnsubscribeContactEmailOtp = async (accountId: number, otp: number): Promise<AccountEmailDomainResponse> =>
	await api.post(`accounts/${accountId}/emails/unsubscribe/verify/`, { otp });

const reportUnsubscribeEmailRequest = async (b64: string, reasonSelectedText: string): Promise<void> =>
	await nonAuthedApi.post(`${WORKSPACE_DOMAINS_ENDPOINT}/report/unsubscribe/`, { b64, reason_selected_text: reasonSelectedText });

const reportSpamEmailRequest = async (b64: string): Promise<void> =>
	await nonAuthedApi.post(`${WORKSPACE_DOMAINS_ENDPOINT}/report/spam/`, { b64 });

export const useAccountEmailDomainCrud = () => {
	const {
		get,
		list,
		create,
		partialUpdate,
		delete: deleteMethod,
	} = buildCrud<AccountEmailDomainId, AccountEmailDomainPayload, AccountEmailDomainResponse>(api, WORKSPACE_DOMAINS_ENDPOINT);
	return {
		get,
		list,
		partialUpdate,
		create,
		deleteMethod,
		verifyDkim,
		sendVerifyEmail,
		verifyReplyToEmailOtp,
		verifyUnsubscribeContactEmailOtp,
		deleteReplyToEmail,
		listReplyToEmails,
		deleteUnsubscribeContactEmail,
		listUnsubscribeContactEmails,
		reportUnsubscribeEmailRequest,
		reportSpamEmailRequest,
	};
};
